import { useState } from 'react'
import { Link, Navigate } from 'react-router'
import { get, post } from '../../api'
import { useAuth } from '../../auth'
import { Alert, Button, Card, Input, Select, Spinner } from '../../components/ui'
import { useAction, useDebounced, useLoad } from '../../hooks'
import type { PlatformBusiness, PlatformStats } from '../../types'

/** The platform operator's view: every restaurant, how busy they are, and the pause switch. */
export function PlatformPage() {
  const { me, signOut } = useAuth()
  const [q, setQ] = useState('')
  const query = useDebounced(q)
  const [status, setStatus] = useState('')
  const stats = useLoad(() => get<PlatformStats>('/v1/platform/stats'), [])
  const list = useLoad(() => get<{ businesses: PlatformBusiness[] }>(`/v1/platform/businesses?${new URLSearchParams({ ...(query ? { q: query } : {}), ...(status ? { status } : {}) })}`), [query, status])
  const setStatusOf = useAction(async (b: PlatformBusiness, next: 'active' | 'paused' | 'archived') => {
    const reason = next === 'active' ? undefined : prompt(`Reason for setting ${b.name} to ${next}?`) ?? undefined
    if (next !== 'active' && reason === undefined) return
    await post(`/v1/platform/businesses/${b.id}/status`, { status: next, reason })
    await Promise.all([list.reload({ quiet: true }), stats.reload({ quiet: true })])
  })
  if (!me) return null
  if (!me.user.isPlatformAdmin) return <Navigate to="/" replace />

  return (
    <div className="shell">
      <main className="main">
        <div className="topbar">
          <Link to="/" className="brand">
            <span className="brand-mark" /> Tabula platform
          </Link>
          <div className="row-flex small">
            <Link to="/">Restaurants</Link>
            <Link to="/account">Account</Link>
            <button className="link-btn" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
        {stats.data ? (
          <div className="stats">
            <div className="stat">
              <div className="stat-label">Active restaurants</div>
              <div className="stat-value">
                {stats.data.businesses.active} <small>of {stats.data.businesses.total}</small>
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Bookings, 30 days</div>
              <div className="stat-value">{stats.data.last30Days.total}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Confirmed</div>
              <div className="stat-value">{stats.data.last30Days.confirmed}</div>
            </div>
            <div className="stat">
              <div className="stat-label">No-shows</div>
              <div className="stat-value">{stats.data.last30Days.noShows}</div>
            </div>
            <div className="stat" style={stats.data.failedNotifications ? { borderColor: 'var(--danger)' } : undefined}>
              <div className="stat-label">Failed messages</div>
              <div className="stat-value">{stats.data.failedNotifications}</div>
            </div>
          </div>
        ) : null}
        <div className="row-flex" style={{ marginBottom: 14 }}>
          <Input placeholder="Search by name or slug" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search restaurants" style={{ maxWidth: 320 }} />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" style={{ width: 'auto' }}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
        {setStatusOf.error ? <Alert>{setStatusOf.error}</Alert> : null}
        {list.error ? <Alert>{list.error}</Alert> : null}
        {list.loading && !list.data ? <Spinner /> : null}
        {list.data ? (
          <Card>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Restaurant</th>
                    <th>Status</th>
                    <th>Team</th>
                    <th>Bookings (30d)</th>
                    <th>Since</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.businesses.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <Link to={`/b/${b.id}/today`} style={{ fontWeight: 600 }}>
                          {b.name}
                        </Link>
                        <div className="muted small">
                          {b.slug} · {b.timezone}
                        </div>
                      </td>
                      <td>
                        <span className={`chip ${b.status === 'active' ? 'chip-confirmed' : b.status === 'paused' ? 'chip-awaiting_approval' : 'chip-finished'}`}>{b.status}</span>
                      </td>
                      <td>{b.members}</td>
                      <td>
                        {b.last30Days.confirmed} <span className="muted small">/ {b.last30Days.bookings}</span>
                      </td>
                      <td className="muted small">{new Date(b.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="row-flex">
                          {b.status !== 'active' ? (
                            <Button size="sm" onClick={() => void setStatusOf.run(b, 'active')}>
                              Activate
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => void setStatusOf.run(b, 'paused')}>
                              Pause
                            </Button>
                          )}
                          {b.status !== 'archived' ? (
                            <Button size="sm" variant="ghost" onClick={() => void setStatusOf.run(b, 'archived')}>
                              Archive
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}
      </main>
    </div>
  )
}
